import type { GroupSubscriptionData } from '@shared/dream-components';
import { useQuery } from '@tanstack/react-query';

import type { GroupSubscriptionsLoaderData } from '~/routes/__loaders/group_subscriptions';

interface Props {
  enabled: boolean;
}

interface ReturnProps {
  isLoading: boolean;
  groupSubscriptions: GroupSubscriptionData[];
}

export const groupSubscriptionsQueryKeys = {
  all: ['group-subscriptions'] as const,
};

/**
 * Read-only React Query fetch of the subscriber's group subscriptions, used by
 * `EffectiveGroupSubscriptionsProvider` for the General tab's group/individual overlap detection.
 * Deliberately separate from the Groups-tab `useGroupSubscriptions` (Remix fetchers + polling +
 * bulk seat mutators): the General tab only needs the raw list, so this avoids coupling it to the
 * heavier groups-tab machinery. Hits the same `/group_subscriptions` loader.
 */
const useGroupSubscriptionsQuery = ({ enabled }: Props): ReturnProps => {
  const { data, isLoading } = useQuery({
    queryKey: groupSubscriptionsQueryKeys.all,
    queryFn: async (): Promise<GroupSubscriptionData[]> => {
      const response = await fetch('/group_subscriptions');
      if (!response.ok) {
        throw new Error('Failed to load group subscriptions');
      }
      // The loader resolves 200 even on a backend failure, surfacing the
      // message in `error` — throw so React Query treats it as a failure.
      const body: GroupSubscriptionsLoaderData = await response.json();
      if (body.error) {
        throw new Error(body.error);
      }
      return body.group_subscriptions;
    },
    enabled,
    // No `staleTime`: data is stale immediately, so when the caller re-enables this
    // query (subscriber returns to the General tab) React Query refetches a fresh
    // list rather than serving the cached one — while still returning the cached
    // groups synchronously so there's no loading flash. `refetchOnWindowFocus` stays
    // off so a plain browser-window refocus doesn't add an extra fetch.
    refetchOnWindowFocus: false,
  });

  return {
    isLoading: isLoading && enabled,
    groupSubscriptions: data ?? [],
  };
};

export default useGroupSubscriptionsQuery;
